import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { setTimeZone } from "../components/format";
import { api, setUnauthorizedHandler, storedToken } from "../api/client";
import type { Me } from "../api/types";

interface AuthState {
  me: Me | null;
  loading: boolean;
  login: (login: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthCtx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);

  // Профиль запрашивается у сервера, а не восстанавливается из хранилища:
  // полномочия могли измениться, а токен — быть отозван.
  const refresh = useCallback(async () => {
    if (!storedToken()) {
      setMe(null);
      setLoading(false);
      return;
    }
    try {
      setMe(withTimeZone(await api.get<Me>("/api/v1/me")));
    } catch {
      setMe(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    setUnauthorizedHandler(() => setMe(null));
    void refresh();
  }, [refresh]);

  const value = useMemo<AuthState>(
    () => ({
      me,
      loading,
      login: async (login, password) => {
        await api.login(login, password);
        setMe(withTimeZone(await api.get<Me>("/api/v1/me")));
      },
      logout: async () => {
        await api.logout();
        setMe(null);
      },
    }),
    [me, loading],
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth вне AuthProvider");
  return ctx;
}

/** Формат времени панели — в часовом поясе из настроек. */
function withTimeZone(me: Me): Me {
  if (me.timezone) setTimeZone(me.timezone);
  return me;
}
